import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { ChatBubbleOutline, ThumbDown, ThumbUp } from '@material-ui/icons';
import { AlertContext } from '../../Context/AlertContextProvider';
import {
  getFeatureRequests,
  getMyFeatureRequests,
  voteFeatureRequest,
} from './featureRequestActions';
import SubmitFeatureRequestDialog from './SubmitFeatureRequestDialog';
import FeatureRequestComments from './FeatureRequestComments';

const STATUS_COLORS = {
  submitted: { background: '#e3f2fd', color: '#1565c0' },
  accepted: { background: '#e8f5e9', color: '#2e7d32' },
  planned: { background: '#ede7f6', color: '#5e35b1' },
  in_progress: { background: '#fff3e0', color: '#ef6c00' },
  shipped: { background: '#e0f2f1', color: '#00695c' },
  rejected: { background: '#ffebee', color: '#c62828' },
};

// Surface active/upcoming work at the top; shipped ("delivered") sorts to the very
// bottom as its own section. Rejected sits just above the delivered divider.
const STATUS_ORDER = {
  accepted: 0,
  in_progress: 1,
  planned: 2,
  submitted: 3,
  rejected: 4,
  shipped: 5,
};

// Stable sort — items keep the server's order (e.g. by votes) within each status.
const sortByStatus = (list) =>
  [...list].sort(
    (a, b) => (STATUS_ORDER[a.status_cd] ?? 3) - (STATUS_ORDER[b.status_cd] ?? 3)
  );

// True for the first shipped item in a status-sorted list — where the "Delivered"
// section begins (shipped items are contiguous at the bottom after sortByStatus).
const isFirstDelivered = (list, idx) =>
  list[idx].status_cd === 'shipped' &&
  (idx === 0 || list[idx - 1].status_cd !== 'shipped');

const renderDeliveredDivider = () => (
  <Box style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 14px' }}>
    <Divider style={{ flex: 1 }} />
    <Typography
      variant="overline"
      style={{ color: '#00695c', fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap' }}
    >
      Delivered
    </Typography>
    <Divider style={{ flex: 1 }} />
  </Box>
);

export default function FeatureRequests() {
  const { handleAlertOpen, setMessage, setMessageType } = useContext(AlertContext);

  const [tab, setTab] = useState(0);
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [votingId, setVotingId] = useState(null);
  const [openCommentsId, setOpenCommentsId] = useState(null);

  const fetchBrowse = () => {
    setLoading(true);
    getFeatureRequests()
      .then((res) => setRequests(sortByStatus(res.data.requests || [])))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  const fetchMine = () => {
    setLoading(true);
    getMyFeatureRequests()
      .then((res) => setMyRequests(sortByStatus(res.data.requests || [])))
      .catch(() => setMyRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === 0) fetchBrowse();
    else fetchMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleVote = (request, value) => {
    // clicking your current vote clears it
    const newVote = request.my_vote === value ? 0 : value;
    setVotingId(request.feature_request_id);
    voteFeatureRequest(request.feature_request_id, newVote)
      .then((res) => {
        setRequests((prev) =>
          prev.map((r) =>
            r.feature_request_id === request.feature_request_id
              ? {
                  ...r,
                  upvotes: res.data.upvotes,
                  downvotes: res.data.downvotes,
                  my_vote: res.data.my_vote,
                }
              : r
          )
        );
      })
      .catch((err) => {
        setMessage(err?.response?.data?.message || 'Error saving vote');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setVotingId(null));
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Typography variant="h4">
          <b>Feature Requests</b>
        </Typography>
        <Button
          variant="contained"
          style={{ background: '#FF682C', color: '#fff' }}
          onClick={() => setIsSubmitOpen(true)}
        >
          Request a Feature
        </Button>
      </Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Vote on features other customers have requested, or submit your own. Votes and
        requests are anonymous to other customers.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        style={{ marginBottom: 16 }}
      >
        <Tab label="Browse" />
        <Tab label="My Requests" />
      </Tabs>

      {tab === 0 && (
        <>
          {requests.map((r, idx) => (
            <React.Fragment key={r.feature_request_id}>
              {isFirstDelivered(requests, idx) && renderDeliveredDivider()}
              <Paper
                variant="outlined"
                style={{
                  padding: 16,
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
              <Box style={{ textAlign: 'center', minWidth: 56 }}>
                <IconButton
                  size="small"
                  disabled={votingId === r.feature_request_id}
                  onClick={() => handleVote(r, 1)}
                  style={{ color: r.my_vote === 1 ? '#2e7d32' : '#bbb' }}
                >
                  <ThumbUp />
                </IconButton>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {r.upvotes}
                </Typography>
                <IconButton
                  size="small"
                  disabled={votingId === r.feature_request_id}
                  onClick={() => handleVote(r, -1)}
                  style={{ color: r.my_vote === -1 ? '#c62828' : '#bbb' }}
                >
                  <ThumbDown />
                </IconButton>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {r.downvotes}
                </Typography>
              </Box>
              <Box style={{ flex: 1 }}>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle1">
                    <b>{r.managed_title}</b>
                  </Typography>
                  <Chip
                    size="small"
                    label={r.status}
                    style={STATUS_COLORS[r.status_cd] || {}}
                  />
                </Box>
                {r.managed_description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}
                  >
                    {r.managed_description}
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<ChatBubbleOutline />}
                  style={{ color: '#888', marginTop: 4, textTransform: 'none' }}
                  onClick={() =>
                    setOpenCommentsId(
                      openCommentsId === r.feature_request_id
                        ? null
                        : r.feature_request_id
                    )
                  }
                >
                  {r.comment_count > 0
                    ? `${r.comment_count} comment${r.comment_count > 1 ? 's' : ''}`
                    : 'Comment'}
                </Button>
                {openCommentsId === r.feature_request_id && (
                  <FeatureRequestComments
                    featureRequestId={r.feature_request_id}
                    onCountChange={(count) =>
                      setRequests((prev) =>
                        prev.map((x) =>
                          x.feature_request_id === r.feature_request_id
                            ? { ...x, comment_count: count }
                            : x
                        )
                      )
                    }
                  />
                )}
              </Box>
              </Paper>
            </React.Fragment>
          ))}
          {!loading && requests.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ padding: 32 }}
            >
              No feature requests are open for voting yet. Be the first to request one!
            </Typography>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {myRequests.map((r, idx) => (
            <React.Fragment key={r.feature_request_id}>
              {isFirstDelivered(myRequests, idx) && renderDeliveredDivider()}
              <Paper
                variant="outlined"
                style={{ padding: 16, marginBottom: 12 }}
              >
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="subtitle1">
                  <b>{r.managed_title || 'Pending review'}</b>
                </Typography>
                <Chip
                  size="small"
                  label={r.status}
                  style={STATUS_COLORS[r.status_cd] || {}}
                />
                <Typography variant="caption" color="textSecondary">
                  submitted{' '}
                  {r.created_dttm
                    ? new Date(r.created_dttm).toLocaleDateString()
                    : ''}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}
              >
                {r.request_details}
              </Typography>
              {r.status_cd === 'rejected' && r.rejection_reason && (
                <Paper
                  variant="outlined"
                  style={{
                    padding: 10,
                    marginTop: 8,
                    background: '#ffebee',
                  }}
                >
                  <Typography variant="body2" style={{ color: '#c62828' }}>
                    <b>Reason:</b> {r.rejection_reason}
                  </Typography>
                </Paper>
              )}
              </Paper>
            </React.Fragment>
          ))}
          {!loading && myRequests.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ padding: 32 }}
            >
              You haven't submitted any feature requests yet.
            </Typography>
          )}
        </>
      )}

      <SubmitFeatureRequestDialog
        isOpen={isSubmitOpen}
        onClose={() => setIsSubmitOpen(false)}
        onSubmitted={() => {
          if (tab === 1) fetchMine();
        }}
      />
    </div>
  );
}
